// The error element of every route (S65): a page that failed to render or load shows this
// inside the shell instead of React Router's developer screen. A chunk missing after a deploy
// reloads the page once, which picks up the new release.
import { ArrowClockwise, WarningCircle } from "@phosphor-icons/react";
import { useEffect } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router";
import { Button } from "rizzui";

import { useT } from "../i18n";
import { EmptyState } from "../ui/states";
import { canReload, isChunkLoadError, reloadOnce } from "./chunkReload";

function describe(error: unknown): string {
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`;
  if (error instanceof Error) return error.message;
  return String(error);
}

export function RouteError({ reload = () => window.location.reload() }: { reload?: () => void }) {
  const t = useT().shell.routeError;
  const error = useRouteError();
  const stale = isChunkLoadError(error);
  // Read once per render; the effect below is what records and performs the reload.
  const reloading = stale && canReload();

  useEffect(() => {
    if (reloading) reloadOnce(reload);
  }, [reloading, reload]);

  if (reloading) {
    return <EmptyState icon={<ArrowClockwise weight="duotone" />} title={t.updating} />;
  }
  return (
    <EmptyState
      icon={<WarningCircle weight="duotone" />}
      title={stale ? t.newVersion : t.failed}
      action={
        <Button className="gap-1.5" onClick={reload}>
          <ArrowClockwise weight="bold" className="h-4 w-4" /> {t.reload}
        </Button>
      }
    >
      {stale ? t.updated : describe(error)}
    </EmptyState>
  );
}
